import React, { useLayoutEffect, useState } from 'react';
import PropTypes from 'prop-types';
import SidePanel from '../SidePanel';
import Button from '../Button';
import { CountBadge } from '../Badge';
import FilterOptionList from './FilterOptionList';
import FilterRange from './FilterRange';
import {
  clearAttribute,
  countActive,
  countForAttribute,
  normalizeValue,
  setRange,
  toggleOption,
} from './filterLogic';

/** Rows a long option list shows before "Show all (N)" in the drawer. */
const DRAWER_LIMIT = 6;

/**
 * FilterDrawer — every attribute in one scrolling sheet (internal): the
 * mobile "Filters" button, and on desktop the "All filters" side panel.
 *
 * Choices are a DRAFT until the sticky "Show N products" button applies
 * them (`onApply(value)`); X, a swipe or the overlay discard it. Each section:
 * heading + count + Clear; long option lists fold to six rows. The result
 * count and the option counts follow the draft (`countResults` / `getCounts`).
 */
const FilterDrawer = ({
  isOpen,
  onClose,
  onApply,
  attributes,
  value,
  getCounts,
  countResults,
  formatValue,
  labels,
  idBase,
}) => {
  const [draft, setDraft] = useState(value);

  // A fresh draft on every open — before paint, so the old one never shows
  useLayoutEffect(() => {
    if (isOpen) setDraft(value);
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const counts = getCounts ? getCounts(draft) : null;
  const total = typeof countResults === 'function' ? countResults(draft) : null;

  let applyLabel = labels.apply;
  if (total === 0) applyLabel = labels.noResults;
  else if (typeof total === 'number') applyLabel = labels.showResults(total);

  const actions = (
    <>
      <Button
        variant="outline"
        onClick={() => setDraft({})}
        disabled={countActive(attributes, draft) === 0}
      >
        {labels.clearAll}
      </Button>
      <Button
        variant="main"
        fullWidth
        disabled={total === 0}
        onClick={() => onApply(normalizeValue(attributes, draft))}
      >
        {applyLabel}
      </Button>
    </>
  );

  return (
    <SidePanel
      isOpen={isOpen}
      onClose={onClose}
      title={labels.filters}
      size="m"
      mobileSheet
      sheetHeader="row"
      closeLabel={labels.close}
      restoreFocus
      actions={actions}
      className="ds-filter-drawer"
    >
      <div className="ds-filter-drawer__sections">
        {attributes.map((attribute) => {
          const headingId = `${idBase}-drawer-${attribute.id}`;
          const n = countForAttribute(attribute, draft[attribute.id]);
          return (
            <section key={attribute.id} className="ds-filter-drawer__section" aria-labelledby={headingId}>
              <div className="ds-filter-drawer__head">
                <h4 id={headingId} className="ds-filter-drawer__title typo-h5">{attribute.label}</h4>
                <CountBadge value={n} />
                <Button
                  size="tertiary"
                  variant="text"
                  className="ds-filter-drawer__clear"
                  disabled={n === 0}
                  onClick={() => setDraft((d) => clearAttribute(d, attribute.id))}
                >
                  {labels.clear}
                </Button>
              </div>

              {attribute.type === 'range' ? (
                <FilterRange
                  attribute={attribute}
                  value={draft[attribute.id]}
                  onCommit={(range) => setDraft((d) => setRange(d, attribute, range))}
                  labels={labels}
                  formatValue={formatValue}
                />
              ) : (
                <div className="ds-filter-drawer__options">
                  <FilterOptionList
                    name={`${idBase}-drawer-opt-${attribute.id}`}
                    legend={attribute.label}
                    options={attribute.options || []}
                    selected={draft[attribute.id] || []}
                    multiple={attribute.multiple !== false}
                    onToggle={(v) => setDraft((d) => toggleOption(d, attribute, v))}
                    counts={counts ? counts[attribute.id] : undefined}
                    searchable={attribute.searchable}
                    limit={DRAWER_LIMIT}
                    labels={labels}
                  />
                </div>
              )}
            </section>
          );
        })}
      </div>
    </SidePanel>
  );
};

FilterDrawer.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  onApply: PropTypes.func.isRequired,
  attributes: PropTypes.array.isRequired,
  value: PropTypes.object.isRequired,
  getCounts: PropTypes.func,
  countResults: PropTypes.func,
  formatValue: PropTypes.func.isRequired,
  labels: PropTypes.object.isRequired,
  idBase: PropTypes.string.isRequired,
};

export default FilterDrawer;
